import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Store, Globe, Power, Truck, ShoppingBag, Package, Eye, Save, Tag, Lock } from 'lucide-react';
import {
  getStorefront, catalogProductsOf, listingFor, updateStorefront, setListing,
} from '../../data/store';

const money = (n) => `₹${Number(n || 0).toFixed(2)}`;
const slugify = (s) => (s || 'store').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const Switch = ({ on, onToggle, disabled, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={onToggle}
    className="relative rounded-full shrink-0 transition-colors disabled:opacity-50"
    style={{ width: 40, height: 22, background: on ? 'var(--theme-primary)' : '#cbd5e1' }}
  >
    <span className="absolute top-0.5 w-[18px] h-[18px] rounded-full bg-white shadow transition-all" style={{ left: on ? '20px' : '2px' }} />
  </button>
);

Switch.propTypes = {
  on: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
};

export const StorefrontPanel = ({ org, showNotification, canManage }) => {
  const accent = org.theme.primary;
  const products = useMemo(() => catalogProductsOf(org.id), [org.id]);

  const [draft, setDraft] = useState(() => {
    const s = getStorefront(org.id);
    return { storeName: s.storeName, tagline: s.tagline, status: s.status, delivery: s.delivery, pickup: s.pickup };
  });
  const [listingMap, setListingMap] = useState(() => {
    const map = {};
    products.forEach((p) => {
      const l = listingFor(org.id, p.id);
      map[p.id] = { published: l ? l.published : false, onlinePrice: l ? l.onlinePrice : (p.basePrice || 0) };
    });
    return map;
  });

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setListingField = (pid, patch) => setListingMap((m) => ({ ...m, [pid]: { ...m[pid], ...patch } }));

  const published = products.filter((p) => listingMap[p.id]?.published);
  const live = draft.status === 'live';

  const save = () => {
    updateStorefront(org.id, draft);
    products.forEach((p) => setListing(org.id, p.id, listingMap[p.id]));
    if (showNotification) showNotification('Storefront saved.', 'success');
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Settings */}
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg p-5 flex flex-col gap-4">
        <header className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Store size={15} style={{ color: accent }} aria-hidden="true" />
            <h3 className="text-[15px] font-semibold tracking-tight text-[#0d0d0d]">Storefront settings</h3>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold uppercase tracking-[0.14em] ${live ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
            <Power size={10} aria-hidden="true" /> {live ? 'Live' : 'Paused'}
          </span>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Store name</span>
            <input type="text" value={draft.storeName} disabled={!canManage} onChange={(e) => setField('storeName', e.target.value)}
              className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] disabled:opacity-60" />
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Tagline</span>
            <input type="text" value={draft.tagline} disabled={!canManage} onChange={(e) => setField('tagline', e.target.value)}
              className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] disabled:opacity-60" />
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            {
              key: 'status', icon: Globe, label: 'Store status', hint: live ? 'Visible to shoppers' : 'Hidden from shoppers', on: live,
              toggle: () => setField('status', live ? 'paused' : 'live')
            },
            {
              key: 'delivery', icon: Truck, label: 'Delivery', hint: 'Offer doorstep delivery', on: draft.delivery,
              toggle: () => setField('delivery', !draft.delivery)
            },
            {
              key: 'pickup', icon: ShoppingBag, label: 'Store pickup', hint: 'Offer in-store pickup', on: draft.pickup,
              toggle: () => setField('pickup', !draft.pickup)
            },
          ].map((row) => (
            <div key={row.key} className="rounded-lg border border-[var(--theme-tertiary)] px-3 py-3 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--theme-secondary)', color: accent }} aria-hidden="true">
                <row.icon size={14} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold text-[#0d0d0d]">{row.label}</p>
                <p className="text-[11px] font-medium text-slate-400 leading-snug">{row.hint}</p>
              </div>
              <Switch on={row.on} disabled={!canManage} onToggle={row.toggle} label={row.label} />
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Product listings */}
        <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg p-5 flex flex-col gap-3">
          <header className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Tag size={14} style={{ color: accent }} aria-hidden="true" />
              <h3 className="text-[15px] font-semibold tracking-tight text-[#0d0d0d]">Product listings</h3>
            </div>
            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">{published.length}/{products.length} online</span>
          </header>
          <div className="flex flex-col divide-y divide-[var(--theme-tertiary)]">
            {products.map((p) => {
              const l = listingMap[p.id] || { published: false, onlinePrice: 0 };
              return (
                <div key={p.id} className="flex items-center gap-3 py-2.5">
                  <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--theme-secondary)', color: accent }} aria-hidden="true">
                    <Package size={15} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-[#0d0d0d] truncate">{p.name}</p>
                    <p className="text-[11px] font-medium text-slate-400">Base {money(p.basePrice)}</p>
                  </div>
                  <label className="flex items-center gap-1">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">₹</span>
                    <input type="number" min="0" step="0.01" value={l.onlinePrice} disabled={!canManage}
                      onChange={(e) => setListingField(p.id, { onlinePrice: e.target.value })}
                      aria-label={`Online price for ${p.name}`}
                      className="w-20 rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-2 py-1.5 text-xs font-bold text-[#0d0d0d] text-right disabled:opacity-60" />
                  </label>
                  <Switch on={l.published} disabled={!canManage} onToggle={() => setListingField(p.id, { published: !l.published })} label={`Publish ${p.name}`} />
                </div>
              );
            })}
            {products.length === 0 && (
              <p className="py-10 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">
                No catalog products to list yet.
              </p>
            )}
          </div>
        </section>

        {/* Live preview */}
        <section className="flex flex-col gap-3">
          <header className="flex items-center gap-2 px-1">
            <Eye size={14} style={{ color: accent }} aria-hidden="true" />
            <h3 className="text-[15px] font-semibold tracking-tight text-[#0d0d0d]">Storefront preview</h3>
          </header>
          <div className="rounded-lg border border-[var(--theme-tertiary)] overflow-hidden bg-white">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--theme-tertiary)]" style={{ background: 'var(--theme-secondary)' }}>
              <span className="flex gap-1" aria-hidden="true">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-300" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
              </span>
              <span className="flex-1 text-center text-[11px] font-semibold text-slate-400 truncate">
                shop.mitra-pos.com/{slugify(draft.storeName)}
              </span>
            </div>
            <div className="px-5 py-6 text-center" style={{ background: accent, color: '#fff' }}>
              <p className="text-lg font-extrabold tracking-tight">{draft.storeName || 'Your Store'}</p>
              <p className="text-[11px] font-medium opacity-85 mt-1">{draft.tagline || 'Shop online with us.'}</p>
              <div className="mt-3 flex items-center justify-center gap-2">
                {draft.delivery && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/15 text-[11px] font-semibold uppercase tracking-[0.12em]"><Truck size={10} /> Delivery</span>}
                {draft.pickup && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/15 text-[11px] font-semibold uppercase tracking-[0.12em]"><ShoppingBag size={10} /> Pickup</span>}
              </div>
            </div>
            {!live && (
              <p className="px-4 py-2 text-center text-xs font-semibold uppercase tracking-[0.14em] text-amber-700 bg-amber-50">
                Store paused — not visible to shoppers
              </p>
            )}
            <div className="p-4 grid grid-cols-2 gap-3" style={{ background: 'var(--theme-secondary)' }}>
              {published.map((p) => (
                <article key={p.id} className="bg-white rounded-lg border border-[var(--theme-tertiary)] p-3 flex flex-col gap-2">
                  <div className="aspect-square rounded-lg flex items-center justify-center" style={{ background: 'var(--theme-secondary)' }}>
                    <Package size={28} strokeWidth={1.4} style={{ color: accent }} aria-hidden="true" />
                  </div>
                  <p className="text-[11px] font-bold text-[#0d0d0d] truncate">{p.name}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-[12px] font-extrabold" style={{ color: accent }}>{money(listingMap[p.id]?.onlinePrice)}</span>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.1em] px-2 py-1 rounded-lg text-white" style={{ background: accent }}>Add</span>
                  </div>
                </article>
              ))}
              {published.length === 0 && (
                <p className="col-span-2 py-8 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">
                  Publish products to fill the storefront
                </p>
              )}
            </div>
          </div>
        </section>
      </div>

      {canManage ? (
        <div className="flex justify-end">
          <button type="button" onClick={save}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] shadow-lg"
            style={{ background: accent, boxShadow: `0 12px 24px -12px ${accent}` }}>
            <Save size={14} aria-hidden="true" /> Save storefront
          </button>
        </div>
      ) : (
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400 inline-flex items-center gap-1.5">
          <Lock size={11} aria-hidden="true" /> Only the organization admin can edit the storefront.
        </p>
      )}
    </div>
  );
};

StorefrontPanel.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
  canManage: PropTypes.bool,
};
